// src/components/preview/PreviewModal.jsx

import React, { useRef, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Tooltip,
  CircularProgress,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import axios from "../../api/axiosInstance";
import InputDialog from "../../components/InputDialog";

export default function PreviewModal({
  open,
  onClose,
  html,
  title = "Document Preview",
  invoiceData,
  mode = "normal",
  templateType = "invoice", // ⭐ NEW (invoice | purchase)
  showSnackbar,
}) {
  const iframeRef = useRef(null);

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [whatsAppDialogOpen, setWhatsAppDialogOpen] = useState(false);

  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Snackbar fallback
  const notify = ({ message, severity }) => {
    if (typeof showSnackbar === "function") {
      showSnackbar({ message, severity });
    } else {
      alert(message);
    }
  };

  // Render HTML in iframe
  useEffect(() => {
    if (iframeRef.current && html) {
      const doc = iframeRef.current.contentDocument;
      doc.open();
      doc.write(html);
      doc.close();
    }
  }, [html, open]);

  // Clean filename
  const getCleanFileName = () => {
    const raw =
      invoiceData?.invoice?.number ||
      invoiceData?.purchase?.number ||
      "document";

    return raw
      .toString()
      .replace(/[^a-zA-Z0-9-_]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_+|_+$/g, "");
  };

  useEffect(() => {
    if (!open || !html) return;

    let cancelled = false;

  


const generatePdf = async () => {
  if (pdfBlob && pdfBase64) return;

  setPdfLoading(true);
  setPdfError(null);

  try {
    let endpoint = "";

    // Select PDF endpoint
    if (templateType === "tripsheet") {
      endpoint = "/api/document-template/tripsheet/pdf";
    } else if (templateType === "purchase") {
      endpoint =
        mode === "compressed"
          ? "/api/document-template/purchase/pdf/compressed"
          : "/api/document-template/purchase/pdf/normal";
    } else {
      if (mode === "compressed") {
        endpoint = "/api/document-template/invoice/pdf/compressed";
      } else if (mode === "contract") {
        endpoint = "/api/document-template/invoice/pdf/contract";
      } else {
        endpoint = "/api/document-template/invoice/pdf/normal";
      }
    }

    // Get the exact HTML displayed in Preview
    let pdfHtml = html;
    const iframe = iframeRef.current;

    if (iframe) {
      try {
        const iframeDoc =
          iframe.contentDocument ||
          iframe.contentWindow?.document;

        if (iframeDoc?.documentElement) {
          pdfHtml =
            "<!DOCTYPE html>\n" +
            iframeDoc.documentElement.outerHTML;
        }
      } catch (e) {
        console.warn(
          "Could not read iframe HTML. Using original HTML.",
          e
        );
      }
    }

    console.log("========== PDF GENERATION ==========");
    console.log("Endpoint:", endpoint);
    console.log("PDF HTML length:", pdfHtml?.length);

    // Send HTML and invoice data to backend
    const res = await axios.post(
      endpoint,
      {
        html: pdfHtml,
        invoiceData: invoiceData || null,
      },
      {
        responseType: "arraybuffer",
      }
    );

    if (cancelled) return;

    // Confirm the response is actually a PDF
    const contentType =
      res.headers?.["content-type"] || "";

    const array = new Uint8Array(res.data);

    const isPdf =
      array.length >= 5 &&
      String.fromCharCode(...array.slice(0, 5)) === "%PDF-";

    if (!isPdf || contentType.includes("application/json")) {
      const responseText = new TextDecoder().decode(array);

      let backendMessage = responseText;

      try {
        const parsed = JSON.parse(responseText);
        backendMessage =
          parsed.error ||
          parsed.message ||
          responseText;
      } catch {
        // Response was not JSON
      }

      throw new Error(
        backendMessage || "The server did not return a valid PDF."
      );
    }

    // Create PDF blob
    const blob = new Blob([array], {
      type: "application/pdf",
    });

    // Convert PDF to Base64
    const base64 = btoa(
      array.reduce(
        (result, byte) =>
          result + String.fromCharCode(byte),
        ""
      )
    );

    const url = URL.createObjectURL(blob);

    setPdfBlob(blob);
    setPdfBase64(base64);
    setPdfUrl(url);
  } catch (err) {
    console.error("PDF Generation Error:", err);

    let errorMessage = "";

    // Decode error response; Axios uses ArrayBuffer here
    try {
      const responseData = err.response?.data;

      if (responseData instanceof ArrayBuffer) {
        errorMessage = new TextDecoder().decode(responseData);
      } else if (responseData instanceof Blob) {
        errorMessage = await responseData.text();
      } else if (typeof responseData === "string") {
        errorMessage = responseData;
      } else if (responseData) {
        errorMessage = JSON.stringify(responseData);
      }
    } catch (decodeError) {
      console.warn(
        "Could not decode PDF error response.",
        decodeError
      );
    }

    // Extract message from backend JSON
    try {
      const parsed = JSON.parse(errorMessage);
      errorMessage =
        parsed.error ||
        parsed.message ||
        errorMessage;
    } catch {
      // Keep the original message
    }

    if (!errorMessage) {
      errorMessage =
        err.message || "Failed to generate PDF.";
    }

    const normalizedError = errorMessage.toLowerCase();

    const isDscError =
      normalizedError.includes("dsc") ||
      normalizedError.includes("token") ||
      normalizedError.includes("certificate") ||
      normalizedError.includes("signing");

    const displayMessage = isDscError
      ? "Insert DSC USB Token"
      : `PDF Error: ${errorMessage}`;

    if (!cancelled) {
      setPdfError(displayMessage);

      notify({
        message: displayMessage,
        severity: "error",
      });
    }
  } finally {
    if (!cancelled) {
      setPdfLoading(false);
    }
  }
};




    // -------------------------------------------------
    // Wait for iframe to finish rendering
    // -------------------------------------------------
    const timer = setTimeout(() => {
      generatePdf();
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, html, templateType, mode]);

  // Cleanup
  useEffect(() => {
    if (!open) {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);

      setPdfUrl(null);
      setPdfBlob(null);
      setPdfBase64(null);
      setPdfError(null);
      setPdfLoading(false);
    }
  }, [open]);

  // ===============================
  // PRINT
  // ===============================
  const handlePrint = () => {
    if (!html) {
      notify({
        message: "Invoice is not ready for printing",
        severity: "error",
      });
      return;
    }

    const printWindow = window.open("", "_blank");

    if (!printWindow) {
      notify({
        message: "Popup blocked",
        severity: "error",
      });
      return;
    }

    try {
      // Write the SAME populated HTML that is shown in Preview
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();

      // Wait for the invoice page and images to render
      const waitForImages = () => {
        const images = Array.from(printWindow.document.images || []);

        if (images.length === 0) {
          setTimeout(() => {
            printWindow.focus();
            printWindow.print();
          }, 300);

          return;
        }

        let loaded = 0;

        const done = () => {
          loaded++;

          if (loaded >= images.length) {
            setTimeout(() => {
              printWindow.focus();
              printWindow.print();
            }, 300);
          }
        };

        images.forEach((img) => {
          if (img.complete) {
            done();
          } else {
            img.onload = done;
            img.onerror = done;
          }
        });
      };

      // Give browser time to create the document
      setTimeout(waitForImages, 200);
    } catch (error) {
      console.error("Print Error:", error);

      try {
        printWindow.close();
      } catch {}

      notify({
        message: "Failed to prepare invoice for printing",
        severity: "error",
      });
    }
  };

  // ===============================
  // DOWNLOAD
  // ===============================
  const handlePDF = () => {
    if (!pdfBlob) {
      notify({ message: "PDF not ready", severity: "error" });
      return;
    }

    const url = pdfUrl || URL.createObjectURL(pdfBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${getCleanFileName()}.pdf`;
    a.click();
  };

  // ===============================
  // EMAIL
  // ===============================
  const handleEmail = () => setEmailDialogOpen(true);

  // ===============================
  // WHATSAPP
  // ===============================

  const handleSendEmail = async (email) => {
    if (!email) return;

    setEmailDialogOpen(false);

    try {
      // -----------------------------------------
      // PDF CHECK
      // -----------------------------------------
      if (!pdfBase64) {
        notify({
          message: "PDF not ready yet",
          severity: "error",
        });
        return;
      }

      // -----------------------------------------
      // CUSTOMER NAME
      // -----------------------------------------
      const customerName =
        invoiceData?.customer?.customername ||
        invoiceData?.customer?.customercompanyname ||
        "";

      if (!customerName.trim()) {
        notify({
          message: "Customer name is missing",
          severity: "error",
        });
        return;
      }

      // -----------------------------------------
      // CLIENT NAME
      // Used for subject only
      // -----------------------------------------
      const clientName = invoiceData?.client?.name || "";

      if (!clientName.trim()) {
        notify({
          message: "Client name is missing",
          severity: "error",
        });
        return;
      }

      // -----------------------------------------
      // INVOICE NUMBER
      // -----------------------------------------
      const invoiceNumber = invoiceData?.invoice?.number || "";

      if (!invoiceNumber.trim()) {
        notify({
          message: "Invoice number is missing",
          severity: "error",
        });
        return;
      }

      // -----------------------------------------
      // INVOICE DATE
      // -----------------------------------------
      const invoiceDate = invoiceData?.invoice?.date || "";

      if (!invoiceDate.trim()) {
        notify({
          message: "Invoice date is missing",
          severity: "error",
        });
        return;
      }

      // -----------------------------------------
      // GET HTML FROM PREVIEW
      // -----------------------------------------
      const iframe = iframeRef.current;

      const iframeDoc =
        iframe?.contentDocument || iframe?.contentWindow?.document;

      if (!iframeDoc) {
        notify({
          message: "Invoice preview is not ready",
          severity: "error",
        });
        return;
      }

      const htmlContent = iframeDoc.documentElement.outerHTML;

      // -----------------------------------------
      // EMAIL SUBJECT
      // -----------------------------------------
      const subject = `${clientName} - Invoice ${invoiceNumber} - ${invoiceDate}`;

      // -----------------------------------------
      // FILE NAME
      // -----------------------------------------
      const fileName = getCleanFileName();

      // -----------------------------------------
      // SEND TO SERVER
      // -----------------------------------------
      await axios.post("/api/send-invoice-email", {
        client_id: invoiceData?.client?.client_id,

        to_email: email,

        subject,

        htmlContent,

        pdfBuffer: pdfBase64,

        fileName,

        // ⭐ IMPORTANT
        customer_name: customerName,

        // Client name also sent if needed
        client_name: clientName,

        // Invoice details
        invoice_number: invoiceNumber,
        invoice_date: invoiceDate,
      });

      notify({
        message: "Email queued successfully!",
        severity: "success",
      });
    } catch (err) {
      console.error("Email Error:", err);

      notify({
        message: err.response?.data?.error || "Failed to queue email",
        severity: "error",
      });
    }
  };

  const handleSendWhatsApp = async (mobile) => {
    if (!mobile) return;
    setWhatsAppDialogOpen(false);

    try {
      if (!pdfBase64) {
        notify({ message: "PDF not ready yet", severity: "error" });
        return;
      }

      await axios.post("/api/whatsapp/send-invoice", {
        client_id: invoiceData?.client?.client_id,
        mobile,
        pdf_url: pdfUrl,
      });

      notify({ message: "Sent via WhatsApp!", severity: "success" });
    } catch (err) {
      console.error("WhatsApp Error:", err);
      notify({ message: "Failed to send WhatsApp", severity: "error" });
    }
  };

  // ===============================
  // UI
  // ===============================
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "80vw",
          maxWidth: "90vw",
          height: "100vh",
          maxHeight: "100vh",
          overflow: "hidden",
          borderRadius: 2,
          p: 4,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          borderBottom: "1px solid #eee",
        }}
      >
        {title}
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: "flex", p: 0 }}>
        {/* TOOLBAR */}
        <Box
          sx={{
            width: "75px",
            background: "#fafafa",
            borderRight: "1px solid #ddd",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 2,
            gap: 2,
          }}
        >
          <Tooltip title="Print">
            <span>
              <IconButton disabled={pdfLoading} onClick={handlePrint}>
                <PrintIcon color="primary" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Download PDF">
            <span>
              <IconButton disabled={pdfLoading} onClick={handlePDF}>
                <PictureAsPdfIcon color="error" />
              </IconButton>
            </span>
          </Tooltip>

          <IconButton onClick={handleEmail} sx={{ background: "#e8f0ff" }}>
            <EmailIcon sx={{ color: "#1976d2" }} />
          </IconButton>

          <IconButton
            sx={{ background: "#e7fff0" }}
            onClick={() => setWhatsAppDialogOpen(true)}
          >
            <WhatsAppIcon sx={{ color: "#25D366" }} />
          </IconButton>

          {/* STATUS */}
          <Box sx={{ mt: 1 }}>
            {pdfLoading && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <CircularProgress size={18} />
                <Typography sx={{ fontSize: "11px" }}>Preparing PDF</Typography>
              </Box>
            )}

            {!pdfLoading && !pdfError && pdfBlob && (
              <Typography sx={{ fontSize: "12px", color: "green" }}>
                PDF Ready
              </Typography>
            )}

            {pdfError && (
              <Typography sx={{ color: "error.main", fontSize: "12px" }}>
                PDF Error
              </Typography>
            )}
          </Box>
        </Box>

        {/* PREVIEW */}
        <Box sx={{ flexGrow: 1, background: "#eee" }}>
          <iframe
            ref={iframeRef}
            srcDoc={html}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              background: "#fff",
            }}
          />
        </Box>
      </DialogContent>

      {/* EMAIL DIALOG */}
      <InputDialog
        open={emailDialogOpen}
        title="Send by Email"
        label="Email"
        placeholder="customer@example.com"
        confirmText="Send"
        onCancel={() => setEmailDialogOpen(false)}
        onConfirm={handleSendEmail}
      />

      {/* WHATSAPP DIALOG */}
      <InputDialog
        open={whatsAppDialogOpen}
        title="Send via WhatsApp"
        label="Mobile Number"
        placeholder="9999999999"
        confirmText="Send"
        onCancel={() => setWhatsAppDialogOpen(false)}
        onConfirm={handleSendWhatsApp}
      />
    </Dialog>
  );
}
